import React from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector, change } from 'redux-form';

import InputField from './InputField';
import CheckboxField from './CheckboxField';
import SelectField from './SelectField';
import SelectSearchField from './SelectSearchField';
import { formatSaveStatus } from '../helpers/formatters';
import { getCarrier, getPrettyCarrier } from '../helpers/getCarrier';
import FormError from './FormError';
import Spinner from './Spinner';
import { isNumber, hasComma } from '../helpers/validators';

const activatioTypesOptions = [
  ['new_activation', 'New Activations'],
  ['mdn_change', 'MDN Changes'],
  ['internal_movement', 'Internal Movement'],
];

const carriersOption = [
  ['att', 'AT&T'],
  ['quick', 'Quick'],
];

const formatInitSubLabel = (sub) => {
  if (!sub) {
    return '';
  }

  const carrier = getCarrier(sub);
  const prettyCarrier = getPrettyCarrier(sub);
  const status = sub[`${carrier}_status`];

  return `${sub.id} | ${sub.first_name} ${sub.last_name} (${prettyCarrier} | ${status})`;
};

let SubForm = ({
  submitting,
  handleSubmit,
  createSub,
  isAttCreateSub,
  isAdminOrManagerOrAgent,
  saveStatus,
  error,
  isAdmin,
  useUserName,
  initialSubs,
  handleChangeActivationType = () => ({}),
  activation_type,
  setInitialSub,
  initialSub,
  initialSubsLoading,
  userManualInitialSub,
  isSimo,
  device_sn,
  device_id,
  simoDevices,
  simoDevicesCount,
  coverageOptions,
  isQuick,
  quickVendors,
  quickSubTypes,
  hideEditCoverage,
}) => {
  const isEditSimo = isSimo && !createSub;

  const isActivationTypeForInitialSub =
    activation_type === 'mdn_change' || activation_type === 'internal_movement';

  // reset initial_carrier_sub field
  if (!isActivationTypeForInitialSub && initialSub && createSub) {
    setInitialSub(null);
  }

  // set initial_carrier_sub on load initial subs
  if (
    isActivationTypeForInitialSub &&
    initialSubs &&
    initialSubs.length &&
    !initialSub
  ) {
    setInitialSub(initialSubs[0].id.toString());
  }

  // reset initial_carrier_sub field when changed activation_type to mdn_change or internal_movement
  if (
    isActivationTypeForInitialSub &&
    initialSubs &&
    !initialSubs.length &&
    initialSub
  ) {
    setInitialSub(null);
  }

  const isActivationTypeForInitialSubs =
    activation_type === 'mdn_change' || activation_type === 'internal_movement';
  const showInitialSubs =
    !userManualInitialSub &&
    isActivationTypeForInitialSubs &&
    initialSubs &&
    Boolean(initialSubs.length);
  const showNoInitialSubsFound =
    !userManualInitialSub &&
    isActivationTypeForInitialSubs &&
    !initialSubsLoading &&
    initialSubs &&
    initialSubs.length === 0;

  const simoDevicesOptions = simoDevices?.length
    ? simoDevices.map((device) => ({
        name: device.serial_number,
        value: device.id.toString(),
      }))
    : [];
  const showDeviceInput = simoDevicesCount > 1000;

  return (
    <form onSubmit={handleSubmit}>
      <Spinner showLoader={submitting}></Spinner>
      {createSub &&
        isSimo &&
        (device_id && device_sn ? (
          <Field
            component={SelectField}
            name="device"
            label="Device SN"
            options={[[device_id, device_sn]]}
            disabled={true}
            readOnly={true}
          />
        ) : showDeviceInput ? (
          <Field component={InputField} name="device" label="Device SN" />
        ) : (
          <Field
            component={SelectSearchField}
            name="device"
            label="Device SN"
            options={simoDevicesOptions}
            placeholder="Select device"
            formName="sub"
          />
        ))}
      <Field component={InputField} name="first_name" label="First Name" />
      <Field component={InputField} name="last_name" label="Last Name" />
      {isSimo && !hideEditCoverage && (
        <Field
          component={SelectField}
          name="coverage"
          label="Coverage"
          options={coverageOptions}
          placeholder="Select region"
          formName="sub"
        />
      )}
      {!createSub && (
        <Field component={InputField} name="agent_id" label="Agent ID" />
      )}
      {(createSub || isAdmin) && (
        <React.Fragment>
          <span className="text-danger">
            NOTE: Very important - you MUST select the appropriate Activation
            Type.
          </span>
          <Field
            component={SelectField}
            name="activation_type"
            label="Activation Type"
            options={activatioTypesOptions}
            empty="Select activation type"
            onChange={handleChangeActivationType}
          />
        </React.Fragment>
      )}
      {isQuick && (
        <Field
          component={SelectField}
          name="vendor"
          label="Vendor"
          options={quickVendors}
          empty="Select vendor"
        />
      )}
      {/* {isQuick && (
        <Field
          component={SelectField}
          name="subscription_type"
          label="Subscription Type"
          options={quickSubTypes}
          empty="Select subscription type"
        />
      )} */}
      {!isEditSimo && showInitialSubs && (
        <Field
          component={SelectField}
          name={`initial_subscription`}
          label="Initial subscription"
          options={initialSubs.map((sub) => [sub.id, formatInitSubLabel(sub)])}
        />
      )}
      {!isEditSimo && initialSubsLoading && !userManualInitialSub && (
        <p>Loading related subscriptions...</p>
      )}
      {!isEditSimo && showNoInitialSubsFound && (
        <p>There is no previous related subscriptions with current user.</p>
      )}
      {!isEditSimo && isActivationTypeForInitialSubs && (
        <Field
          component={CheckboxField}
          name="use_manual_initial_sub"
          label="Add initial subscription manually"
        />
      )}
      {!isEditSimo &&
        isActivationTypeForInitialSubs &&
        userManualInitialSub && (
          <React.Fragment>
            <div className="row">
              <div className="col-md-6">
                <Field
                  component={InputField}
                  name={`initial_subscription_manual`}
                  label="Initial subscription id"
                />
              </div>
              <div className="col-md-6">
                <Field
                  component={SelectField}
                  name={`initial_subscription_manual_carrier`}
                  label="Initial subscription carrier"
                  options={carriersOption}
                  empty="Select carrier"
                />
              </div>
            </div>
          </React.Fragment>
        )}

      {!isSimo && (
        <Field
          component={CheckboxField}
          name="sms_notifications"
          label={'SMS Notifications'}
        />
      )}

      {error && <FormError error={error} />}
      <div>
        <button
          type="submit"
          className="btn btn-primary mar-r"
          disabled={saveStatus === 'PENDING'}
        >
          Save Subscription
        </button>
        {createSub && (
          <button
            type="button"
            className="btn btn-default"
            onClick={useUserName}
          >
            Use account name
          </button>
        )}
      </div>
      <span className="submit-status">{formatSaveStatus(saveStatus)}</span>
    </form>
  );
};

const validate = (
  values,
  { createSub, initialValues, coverageOptions, isQuick },
) => {
  let err = {};

  if (values.first_name && hasComma(values.first_name)) {
    err.first_name = 'Comma sign is not allowed';
  }

  if (values.last_name && hasComma(values.last_name)) {
    err.last_name = 'Comma sign is not allowed';
  }

  // simo
  if (coverageOptions && coverageOptions.length) {
    if (!values.device) {
      err.device = 'Required';
    }
  }

  if (
    createSub ||
    (!createSub && initialValues && initialValues.activation_type)
  ) {
    if (!values.activation_type) {
      err.activation_type = 'Required';
    }
  }

  if (isQuick) {
    if (!values.vendor) {
      err.vendor = 'Required';
    }
    // if (!values.subscription_type) {
    //   err.subscription_type = 'Required';
    // }
  }

  if (values.use_manual_initial_sub) {
    if (!values.initial_subscription_manual) {
      err.initial_subscription_manual = 'Required';
    } else if (values.initial_subscription_manual) {
      err.initial_subscription_manual = isNumber(
        values.initial_subscription_manual,
      );
    }

    if (!values.initial_subscription_manual_carrier) {
      err.initial_subscription_manual_carrier = 'Required';
    }
  }

  return err;
};

const selector = formValueSelector('sub');
const mapStateToProps = (state, ownProps) => {
  return {
    activation_type: selector(state, 'activation_type'),
    initialSub: selector(state, `initial_subscription`),
    userManualInitialSub: selector(state, 'use_manual_initial_sub'),
  };
};

const mapDispathToProps = (dispatch) => ({
  setInitialSub: (value) =>
    dispatch(change('sub', `initial_subscription`, value)),
});

SubForm = reduxForm({
  form: 'sub',
  validate,
  enableReinitialize: true,
})(SubForm);

export default connect(mapStateToProps, mapDispathToProps)(SubForm);

export const mapSubToValues = ({
  first_name,
  last_name,
  agent_id,
  activation_type,
  sub_carrier,
  sms_notifications,
  coverage,
}) => ({
  first_name,
  last_name,
  agent_id,
  activation_type: activation_type ? activation_type : undefined,
  sub_carrier,
  sms_notifications,
  coverage,
});
